import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { UserDefined } from '@docma-mm-core/user-defined/user-defined';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsProjectSpecificFilterService implements OnDestroy {
  
  private currentProjectId: number;
  private projectSpecific1Subject = new BehaviorSubject<SelectItem[]>([]);
  private projectSpecific2Subject = new BehaviorSubject<SelectItem[]>([]);
  private destroy$ = new Subject<void>();
  
  public projectSpecific1$ = this.projectSpecific1Subject.asObservable();
  public projectSpecific2$ = this.projectSpecific2Subject.asObservable();
  
  constructor(
    private readonly mmViewService: MmViewService,
    private readonly mmDbService: MmDbService,
    private readonly translateService: TranslateService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadProjectData(project.MyDocmaUid);
      });
  }

  private async loadProjectData(projectMyDocmaUid: string): Promise<void> {
    try {
      const db = await this.mmDbService.getProjectDb(projectMyDocmaUid);
      
      await Promise.all([
        this.loadProjectSpecific1(db),
        this.loadProjectSpecific2(db)
      ]);
    } catch (error) {
      this.logger.error(error, '', 'FieldsProjectSpecificFilterService');
    }
  }

  private async loadProjectSpecific1(db: any): Promise<void> {
    try {
      const userDefineds = await db.get(MmProjectDbCollections.userDefineds as never, 'Type1') as UserDefined[];
      const selectItems = this.formatProjectSpecificForDropdown(userDefineds || []);
      this.projectSpecific1Subject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsProjectSpecificFilterService');
      this.projectSpecific1Subject.next([]);
    }
  }

  private async loadProjectSpecific2(db: any): Promise<void> {
    try {
      const userDefineds = await db.get(MmProjectDbCollections.userDefineds as never, 'Type2') as UserDefined[];
      const selectItems = this.formatProjectSpecificForDropdown(userDefineds || []);
      this.projectSpecific2Subject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsProjectSpecificFilterService');
      this.projectSpecific2Subject.next([]);
    }
  }

  private formatProjectSpecificForDropdown(userDefineds: UserDefined[]): SelectItem[] {
    const selectItems: SelectItem[] = [];
    
    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });
    
    const sortedUserDefineds = userDefineds
      .filter(userDefined => userDefined.Id)
      .sort((a, b) => (a.Name1 || '').localeCompare(b.Name1 || ''));
    
    sortedUserDefineds.forEach(userDefined => {
      const code = userDefined.Name1 || '';
      const description = userDefined.Name2 || '';
      
      let displayText: string;
      if (code && description) {
        displayText = `${code} - ${description}`;
      } else if (code) {
        displayText = code;
      } else if (description) {
        displayText = description;
      } else {
        this.logger.error('[formatProjectSpecificForDropdown] No display text found for user defined', '', 'FieldsProjectSpecificFilterService');
        return;
      }
      
      selectItems.push({
        id: userDefined.Id.toString(),
        text: displayText
      });
    });
    
    return selectItems;
  }

  public getProjectSpecific1DisplayNameById(projectSpecificId: string): string {
    const projectSpecifics = this.projectSpecific1Subject.value;
    const projectSpecific = projectSpecifics.find(ps => ps.id === projectSpecificId);
    if (!projectSpecific) {
      this.logger.error('[getProjectSpecific1DisplayNameById] No project specific found for id: ' + projectSpecificId, '', 'FieldsProjectSpecificFilterService');
      throw new Error('[getProjectSpecific1DisplayNameById] No project specific found for id: ' + projectSpecificId);
    }
    return projectSpecific.text;
  }

  public getProjectSpecific2DisplayNameById(projectSpecificId: string): string {
    const projectSpecifics = this.projectSpecific2Subject.value;
    const projectSpecific = projectSpecifics.find(ps => ps.id === projectSpecificId);
    if (!projectSpecific) {
      this.logger.error('[getProjectSpecific2DisplayNameById] No project specific found for id: ' + projectSpecificId, '', 'FieldsProjectSpecificFilterService');
      throw new Error('[getProjectSpecific2DisplayNameById] No project specific found for id: ' + projectSpecificId);
    }
    return projectSpecific.text;
  }
}